import { useEffect, useCallback, useMemo, useState, useContext } from "react";
import { Box, IconButton, ListItemText, CircularProgress } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useLocation } from "react-router-dom";
import { TopBarContext } from "../../providers/TopBarProvider";
import { ToastContext } from "../../providers/ToastProvider";
import ClientBranchService from "../../services/ClientBranchService";
import CustomNoRows from "../atoms/CustomNoRows";

const ClientBranches = () => {
  const apiRef = useGridApiRef();
  const [branches, setBranches] = useState([]);
  const [error, setError] = useState(false);
  const [itemsLoading, setItemsLoading] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { setTitle, setSearchOptions, setBtnPrimary } =
    useContext(TopBarContext);
  const { setShowToast, setToastMessage } = useContext(ToastContext);
  const clientBranchService = useMemo(() => new ClientBranchService(), []);
  const navigate = useNavigate();
  const location = useLocation();
  
  const clientId = Number(location.pathname.split("/")[2]);

  const columns = [
    {
      field: "branchName",
      headerName: "Sucursal",
      minWidth: 180,
      flex: 1,
      sorting: false,
      editable: true,
    },
    {
      field: "address",
      headerName: "Dirección",
      minWidth: 200,
      flex: 1,
      sorting: false,
      editable: true,
    },
    {
      field: "personInChargeName",
      headerName: "Encargado",
      minWidth: 200,
      flex: 1,
      sorting: false,
      editable: true,
      renderCell: (item) => (
        <ListItemText
          primary={item.row.personInChargeName}
          primaryTypographyProps={{
            fontSize: "1.1rem",
          }}
          secondary={item.row.personInChargePhone}
        />
      ),
    },
    {
      field: "edit",
      headerName: "Acciones",
      sortable: false,
      width: 135,
      renderCell: (row) => {
        const item = itemsLoading.find((i) => i.id === row.id);
        if (item?.id && item?.edit) {
          return (
            <Box mr={1}>
              Editando{" "}
              <CircularProgress
                color="primary"
                size={25}
                sx={{ verticalAlign: "text-bottom" }}
              />
            </Box>
          );
        }

        if (item?.id && !item?.edit) {
          return (
            <Box mr={1}>
              Eliminando{" "}
              <CircularProgress
                color="error"
                size={25}
                sx={{ verticalAlign: "text-bottom" }}
              />
            </Box>
          );
        }

        return (
          <>
            <IconButton
              onClick={() => navigate(`/clients/${clientId}/branches/edit-branch?id=${row.id}`, { state: row.row })}
              sx={{ float: "right" }}
            >
              <Box
                component={FontAwesomeIcon}
                icon="pencil"
                fontSize="1rem"
                color="secondary.main"
              />
            </IconButton>
            <IconButton
              onClick={() => deleteBranch(row.id, row.row.branchName)}
              sx={{ float: "right", ml: 2 }}
            >
              <Box
                component={FontAwesomeIcon}
                icon="trash"
                fontSize="1rem"
                color="error.main"
              />
            </IconButton>
          </>
        );
      },
    },
  ];

  const getBranches = useCallback(
    async () => {
      try {
        setIsLoading(true);

        const res = await clientBranchService.getBranchesByClientId(clientId);
        const items = res.data.result;

        setBranches(items.map((item) => ({
          ...item,
          id: Number(item.id),
          selectable: true,
        })));
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [clientBranchService, clientId]
  );

  const editBranch = async (id, field, oldValue, newValue) => {
    try {
      if (newValue === oldValue) {
        return;
      }

      setItemsLoading((oldItems) => [
        ...oldItems,
        {
          id,
          edit: true,
        },
      ]);

      const payload = {
        id,
        [field]: newValue,
      };

      const res = await clientBranchService.update(payload);

      if (!res.data.success) {
        return;
      }

      setBranches((oldBranches) => {
        const idx = oldBranches.findIndex((branch) => branch.id === id);
        oldBranches[idx][field] = newValue;
        return [...oldBranches];
      });

      setToastMessage([
        {
          severity: "success",
          primary: `Sucursal actualizada exitosamente`,
        },
      ]);
      setShowToast(true);
    } catch (error) {
      setBranches((oldBranches) => [...oldBranches]);

      setToastMessage([
        {
          severity: "error",
          primary: `Error al intentar editar la sucursal`,
        },
      ]);
      setShowToast(true);
    } finally {
      setItemsLoading((oldItems) => [
        ...oldItems.filter((item) => item.id !== id),
      ]);
    }
  };

  const deleteBranch = async (id, branchName) => {
    try {
      setItemsLoading((oldItems) => [
        ...oldItems,
        {
          id,
          edit: false,
        },
      ]);

      const res = await clientBranchService.delete(id);

      if (!res.data.success) {
        return;
      }

      setBranches((oldBranches) => [
        ...oldBranches.filter((branch) => branch.id !== id),
      ]);

      setToastMessage([
        {
          severity: "success",
          primary: `Sucursal ${branchName} eliminada exitosamente`,
        },
      ]);
      setShowToast(true);
    } catch (error) {
      setToastMessage([
        {
          severity: "error",
          primary: `Error al intentar eliminar la sucursal ${branchName}`,
        },
      ]);
      setShowToast(true);
    } finally {
      setItemsLoading((oldItems) => [
        ...oldItems.filter((item) => item.id !== id),
      ]);
    }
  };

  useEffect(() => {
    setTitle("Sucursales");
    setSearchOptions({
      action: () => null,
      hidden: true,
    });
    setBtnPrimary({
      label: "Agregar Sucursal",
      action: () => navigate(`/clients/${clientId}/branches/new-branch`),
      icon: "plus",
    });
  }, [setTitle, setSearchOptions, setBtnPrimary, navigate, clientId]);

  useEffect(() => {
    if (clientId && !isNaN(clientId)) {
      getBranches();
    } else {
      navigate("/clients");
    }
  }, [clientId, getBranches, navigate]);

  const classes = {
    root: {
      height: "70vh",
      width: "100%",
      mt: 5,
    },
    datagrid: {
      marginTop: 5,
      fontSize: "1.1rem",
      ".MuiDataGrid-cell--editing .MuiListItemText-root": {
        fontSize: "1.1rem",
      },
      ".MuiDataGrid-cell--editing input": {
        fontSize: "1.1rem",
      },
      ".MuiDataGrid-cell:focus,.MuiDataGrid-cell:focus-within": {
        outline: "none",
      },
    },
  };

  return (
    <Box sx={classes.root}>
      <DataGrid
        sx={classes.datagrid}
        apiRef={apiRef}
        columns={columns}
        rows={branches}
        editMode="cell"
        getRowId={(row) => row.id}
        rowHeight={68}
        disableColumnMenu
        disableRowSelectionOnClick
        isRowSelectable={(item) => item.row.selectable}
        isCellEditable={(item) => item.row.selectable}
        pageSizeOptions={[10]}
        loading={isLoading}
        localeText={{
          toolbarColumns: "Columnas",
          toolbarColumnsLabel: "Seleccionar columnas",
          noRowsLabel: "Sin Sucursales",
          footerRowSelected: (count) =>
            count !== 1
              ? `${count.toLocaleString()} filas seleccionadas`
              : `${count.toLocaleString()} fila seleccionada`,
        }}
        slots={{
          noRowsOverlay: () => (
            <CustomNoRows
              message={
                error
                  ? "Error intentando obtener las sucursales"
                  : "No hay sucursales registradas"
              }
            />
          ),
          noResultsOverlay: () => (
            <CustomNoRows
              message={
                error
                  ? "Error intentando obtener las sucursales"
                  : "No hay sucursales registradas"
              }
            />
          ),
        }}
        processRowUpdate={(newRow, oldRow) => {
          const changedField = Object.keys(newRow).find(
            (key) => newRow[key] !== oldRow[key]
          );
          if (changedField) {
            editBranch(newRow.id, changedField, oldRow[changedField], newRow[changedField]);
          }
          return newRow;
        }}
      />
    </Box>
  );
};

export default ClientBranches;

